import type { ReactNode } from "react";

import type { AddressSnapshot, OrderHistory, PanelOrderItem } from "@motolynk/api-client";
import { formatDateTime } from "@motolynk/app-core";
import { useTranslation } from "@motolynk/i18n";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  formatMoney,
  MediaThumb,
  Separator,
  StatusBadge,
} from "@motolynk/ui";

/** Order screens' building blocks, shared by the vendor panel (P5-01) and the admin panel (P5-02). */

export const FLOW = [
  "pending",
  "confirmed",
  "processing",
  "packed",
  "shipped",
  "delivered",
] as const;

export function OrderStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation("orders");
  return <StatusBadge status={status} label={t(`status.${status}`)} />;
}

/** The fulfilment steps as a progress line (done / current / to do; cancelled shows where it stopped). */
export function StatusSteps({ status, cancelled }: { status: string; cancelled: boolean }) {
  const { t } = useTranslation("orders");
  const reached = FLOW.indexOf(status as (typeof FLOW)[number]);
  return (
    <ol className="flex flex-wrap items-center gap-2 text-xs" aria-label={t("detail.progress")}>
      {FLOW.map((step, index) => {
        const done = !cancelled && index <= reached;
        return (
          <li key={step} className="flex items-center gap-2">
            <Badge tone={done ? (index === reached ? "brand" : "success") : "neutral"}>
              {t(`status.${step}`)}
            </Badge>
            {index < FLOW.length - 1 && (
              <span className="h-px w-4 bg-border" aria-hidden>
                {" "}
              </span>
            )}
          </li>
        );
      })}
      {cancelled && (
        <li>
          <OrderStatusBadge status="cancelled" />
        </li>
      )}
    </ol>
  );
}

export function ItemsCard({
  items,
  showCommission,
}: {
  items: readonly PanelOrderItem[];
  showCommission?: boolean;
}) {
  const { t, i18n } = useTranslation("orders");
  const money = (value: string) => formatMoney(value, "INR", i18n.language);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("detail.items", { count: items.length })}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-border">
          {items.map((item) => (
            <li key={item.uid} className="flex gap-3 py-3 first:pt-0 last:pb-0">
              <MediaThumb
                className="size-14 shrink-0 rounded-lg"
                item={{
                  kind: "image",
                  thumbnailUrl: item.image_url || null,
                  title: item.product_title,
                }}
              />
              <div className="min-w-0 flex-1">
                <p className="font-medium">{item.product_title}</p>
                <p className="text-xs text-muted-foreground">
                  {[item.variant_name, t("detail.sku", { sku: item.sku }), item.mpn]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                <p className="mt-1 text-sm">
                  {t("detail.qtyPrice", { quantity: item.quantity, price: money(item.unit_price) })}
                  {Number(item.discount_amount) > 0 &&
                    ` · ${t("detail.lineDiscount", { amount: money(item.discount_amount) })}`}
                </p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {item.quantity_cancelled > 0 && (
                    <Badge tone="danger">
                      {t("detail.cancelledQty", { count: item.quantity_cancelled })}
                    </Badge>
                  )}
                  {item.quantity_returned > 0 && (
                    <Badge tone="neutral">
                      {t("detail.returnedQty", { count: item.quantity_returned })}
                    </Badge>
                  )}
                </div>
              </div>
              <div className="text-right text-sm">
                <p className="font-semibold">{money(item.line_total)}</p>
                <p className="text-xs text-muted-foreground">
                  {t("detail.gst", { rate: Number(item.tax_rate), amount: money(item.tax_amount) })}
                </p>
                {showCommission && (
                  <p className="text-xs text-muted-foreground">
                    {t("detail.commissionLine", { amount: money(item.commission_amount) })}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export function AddressCard({ address, note }: { address: AddressSnapshot; note?: string }) {
  const { t } = useTranslation("orders");
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("detail.shipTo")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">
        <p className="font-medium">{address.full_name}</p>
        <p>{address.line1}</p>
        {address.line2 && <p>{address.line2}</p>}
        {address.landmark && <p>{t("detail.landmark", { landmark: address.landmark })}</p>}
        <p>
          {address.city}, {address.state} {address.postal_code}
        </p>
        <p className="text-muted-foreground">{t("detail.phone", { phone: address.phone })}</p>
        {note && (
          <>
            <Separator className="my-3" />
            <p className="text-xs font-medium uppercase text-muted-foreground">
              {t("detail.customerNote")}
            </p>
            <p>{note}</p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** Label / amount rows; `strong` marks the total line. */
export function AmountsCard({
  title,
  rows,
  footer,
}: {
  title: string;
  rows: { label: string; value: string; strong?: boolean; hidden?: boolean }[];
  footer?: ReactNode;
}) {
  const { i18n } = useTranslation("orders");
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="space-y-2 text-sm">
          {rows
            .filter((row) => !row.hidden)
            .map((row) => (
              <div
                key={row.label}
                className={
                  row.strong
                    ? "flex justify-between border-t border-border pt-2 font-semibold"
                    : "flex justify-between"
                }
              >
                <dt className={row.strong ? undefined : "text-muted-foreground"}>{row.label}</dt>
                <dd>{formatMoney(row.value, "INR", i18n.language)}</dd>
              </div>
            ))}
        </dl>
        {footer}
      </CardContent>
    </Card>
  );
}

export function HistoryCard({
  history,
  title,
}: {
  history: readonly OrderHistory[];
  title?: string;
}) {
  const { t, i18n } = useTranslation("orders");
  const status = (value: string) => (value ? t(`status.${value}`, { defaultValue: value }) : "");
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title ?? t("detail.history")}</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="space-y-4 text-sm">
          {[...history].reverse().map((row, index) => (
            <li key={`${row.created_at}-${index}`} className="border-l-2 border-border pl-4">
              <p className="font-medium">
                {row.from_status && row.from_status !== row.to_status
                  ? t("detail.historyFrom", {
                      from: status(row.from_status),
                      to: status(row.to_status),
                    })
                  : status(row.to_status)}
              </p>
              <p className="text-muted-foreground">
                {formatDateTime(row.created_at, i18n.language)} ·{" "}
                {row.actor_name || t(`actor.${row.actor_type}`, { defaultValue: row.actor_type })}
              </p>
              {row.note && <p className="mt-1">{row.note}</p>}
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}
